import { CSSResult, LitElement, TemplateResult, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import * as THREE from 'three';
import css from "./maz-plushie.scss";

export const PlushieDisplay2D = "2D";
export const PlushieDisplay3D = "3D";

@customElement("maz-plushie")
export class MaziCraftPlushie extends LitElement {
  static get styles(): CSSResult { return css; }

  /**
   * The display mode
   */
  @property({type: String, reflect: true}) mode = PlushieDisplay2D;

  /**
   * The texture to use
   */
  @property({type: String, reflect: true}) skinUrl = "";

  /**
   * The name to display
   */
  @property({type: String, reflect: true}) name = "";

  /**
   * Hide the controls
   */
  @property({type: Boolean, reflect: true}) hideControls = false;

  /**
   * Hide the link
   */
  @property({type: Boolean, reflect: true}) hideLink = false;

  /**
   * The playing state
   */
  @state() isPlaying = true;

  /**
   * Reference to the canvas element
   */
  @query('canvas') private canvas!: HTMLCanvasElement;

  /**
   * Reference to the play button
   */
  @query('button#play') private playButton!: HTMLButtonElement;

  // Three.js object for rendering
  private renderer!: THREE.WebGLRenderer;
  private scene!: THREE.Scene;
  private camera!: THREE.PerspectiveCamera;
  private plushie3d!: THREE.Group;
  private plushie2d!: THREE.Group;
  private skinTexture!: THREE.Texture;

  // State management for animation and interaction
  private isDragging = false;
  private lastX = 0;
  private lastY = 0;
  private rotationX = 0;
  private rotationY = 0;
  private autoRotate = true;
  private animationFrame: number = 0;

  /**
   * Called when the plushie component is first attached to the DOM
   */
  firstUpdated() {
    this.initialise();
    this.loadPlushie();
  }

  /**
   * Called when the plushie component is detached from the DOM
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    cancelAnimationFrame(this.animationFrame);
  }

  /**
   * Called when a property changes
   * @param changedProps 
   */
  protected updated(changedProps: Map<string, unknown>) {
    if(changedProps.has("skinUrl") && this.skinUrl) {
      this.updateTexture();
    }
  }

  /**
   * Method to initialise the scene, renderer and camera
   */
  private initialise() {
    // Set up scene
    this.scene = new THREE.Scene();

    // Set up rendered
    this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true, alpha: true });
    const rect = this.canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    this.renderer.setSize(rect.width * dpr, rect.height * dpr, false);
    this.renderer.setPixelRatio(dpr);    
    this.renderer.toneMapping = THREE.NoToneMapping;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;

    // Set up camera
    this.camera = new THREE.PerspectiveCamera(70, rect.width / rect.height, 0.1, 1000);
    this.camera.position.set(0, 0, 1.75);
    this.camera.lookAt(0, 0, 0);
  }

  /**
   * Method to load the plushie
   */
  private loadPlushie() {
    this.loadTexture();
    this.createModels();
    this.startAnimation();
  }

  /**
   * Method to load the texture
   */
  private loadTexture() {
    this.skinTexture = new THREE.TextureLoader().load(this.skinUrl);
    this.skinTexture.magFilter = THREE.NearestFilter;
    this.skinTexture.minFilter = THREE.NearestFilter;
    this.skinTexture.colorSpace = THREE.SRGBColorSpace;
  }

/**
 * Method to reload the texture and update all materials in the scene
 */
private updateTexture() {
  // Dispose of the old texture to free GPU memory
  if(this.skinTexture) {
    this.skinTexture.dispose();
  }

  // Load new texture
  const loader = new THREE.TextureLoader();
  loader.load(this.skinUrl, (newTexture) => {
    newTexture.magFilter = THREE.NearestFilter;
    newTexture.minFilter = THREE.NearestFilter;
    newTexture.colorSpace = THREE.SRGBColorSpace;
    this.skinTexture = newTexture;

    // Update all materials that used the old texture
    this.scene.traverse((child) => {
      if((child as THREE.Mesh).isMesh) {
        const mesh = child as THREE.Mesh;
        const mat = mesh.material as THREE.MeshBasicMaterial;
        if (mat.map) {
          mat.map = this.skinTexture;
          mat.needsUpdate = true;
        }
      }
    });

    // Re-render with new texture
    this.renderer.render(this.scene, this.camera);
  });
}

  /**
   * Method to create the 3D and 2D models for the plushie
   */
  private createModels() {
    this.create3DPlushie();
    this.create2DPlushie();
    this.setView();
  }

  /**
   * Method to create a cube textured using the portion of the skin specified by the offset
   * @param offsetX The x offset from bottom left
   * @param offsetY The y offset form bottom left
   * @returns THREE.Group
   */
  private createCube(offsetX: number, offsetY: number): THREE.Group {
    const group = new THREE.Group();

    const faces: { pos: [number, number, number]; rot: [number, number, number]; rect: [number, number, number, number]; }[] = [
      { pos: [0, 0.5, 0], rot: [-Math.PI / 2, 0, 0], rect: [offsetX + 8, offsetY + 8, offsetX + 16, offsetY + 16] }, // top
      { pos: [0, -0.5, 0], rot: [Math.PI / 2, 0, Math.PI], rect: [offsetX + 16, offsetY + 8, offsetX + 24, offsetY + 16] }, // bottom
      { pos: [0, 0, 0.5], rot: [0, 0, 0], rect: [offsetX + 8, offsetY, offsetX + 16, offsetY + 8] }, // front
      { pos: [0, 0, -0.5], rot: [0, Math.PI, 0], rect: [offsetX + 24, offsetY, offsetX + 32, offsetY + 8] }, // back
      { pos: [-0.5, 0, 0], rot: [0, Math.PI / 2, 0], rect: [offsetX + 16, offsetY, offsetX + 24, offsetY + 8] }, // left
      { pos: [0.5, 0, 0], rot: [0, -Math.PI / 2, 0], rect: [offsetX, offsetY, offsetX + 8, offsetY + 8] }, // right
    ];

    for (const f of faces) {
      const [x1, y1, x2, y2] = f.rect;
      const uMin = x1 / 64, uMax = x2 / 64;
      const vMin = y1 / 64, vMax = y2 / 64;

      const geom = new THREE.PlaneGeometry(1, 1);
      const uvs = geom.attributes.uv as THREE.BufferAttribute;
      uvs.setXY(0, uMin, vMax);
      uvs.setXY(1, uMax, vMax);
      uvs.setXY(2, uMin, vMin);
      uvs.setXY(3, uMax, vMin);
      uvs.needsUpdate = true;

      const mat = new THREE.MeshBasicMaterial({
        map: this.skinTexture,
        transparent: true,
        side: THREE.DoubleSide
      });

      const plane = new THREE.Mesh(geom, mat);
      plane.position.set(...f.pos);
      plane.rotation.set(...f.rot);
      group.add(plane);
    }

    return group;
  }

  /**
   * Method to create the 3D plushie as 2 overlaid cubes
   */
  private create3DPlushie() {
    this.plushie3d = new THREE.Group();

    // Create base layer of the plushie
    const baseLayer = this.createCube(0, 48);
    this.plushie3d.add(baseLayer);

    // Create outer layer of the plushie
    const outerLayer = this.createCube(32, 48);
    outerLayer.scale.set(1.05, 1.05, 1.05);
    this.plushie3d.add(outerLayer);

    this.scene.add(this.plushie3d);
  }

  /**
   * Method to create the 2D plushie as 2 overlaid planes showing only the front face
   */
  private create2DPlushie() {
    this.plushie2d = new THREE.Group();

    // Create base layer of the plushie
    const baseGeometry = new THREE.PlaneGeometry(1, 1);
    const baseMaterial = new THREE.MeshBasicMaterial({ map: this.skinTexture, transparent: true });
    const baseUV = baseGeometry.attributes.uv as THREE.BufferAttribute;
    baseUV.setXY(0, 8/64, 56/64);
    baseUV.setXY(1, 16/64, 56/64);
    baseUV.setXY(2, 8/64, 48/64);
    baseUV.setXY(3, 16/64, 48/64);
    baseUV.needsUpdate = true;
    const baseMesh = new THREE.Mesh(baseGeometry, baseMaterial);
    this.plushie2d.add(baseMesh);

    // Create outer layer of the plushie
    const outerGeometry = new THREE.PlaneGeometry(1, 1);
    const outerMaterial = new THREE.MeshBasicMaterial({ map: this.skinTexture, transparent: true });
    const outerUV = outerGeometry.attributes.uv as THREE.BufferAttribute;
    outerUV.setXY(0, 40/64, 56/64);
    outerUV.setXY(1, 48/64, 56/64);
    outerUV.setXY(2, 40/64, 48/64);
    outerUV.setXY(3, 48/64, 48/64);
    outerUV.needsUpdate = true;
    const outerMesh = new THREE.Mesh(outerGeometry, outerMaterial);
    outerMesh.position.z = 0.075;
    this.plushie2d.add(outerMesh);

    this.plushie2d.visible = this.mode == "2D";
    this.scene.add(this.plushie2d);
  }

  /**
   * Method to set the view based on the display mode
   */
  private setView() {
    if(this.mode == PlushieDisplay2D) {
      this.plushie2d.visible = true;
      this.plushie3d.visible = false;
      this.camera.position.set(0, 0, 1.25);
      this.isPlaying = true;
      this.playButton.classList.add("hidden");
    } else {
      this.plushie2d.visible = false;
      this.plushie3d.visible = true;
      this.isPlaying = true;
      this.autoRotate = true;
      this.camera.position.set(0, 0, 1.75);
      this.playButton.classList.remove("hidden");
    }
    this.playButton.classList.add("paused");
  }

  /**
   * Method to start the animation of the 3D plushie
   */
  private startAnimation = () => {
    this.animationFrame = requestAnimationFrame(this.startAnimation);

    if (this.mode == PlushieDisplay3D && this.plushie3d && this.autoRotate) {
      this.rotationX *= 0.9;
      this.plushie3d.rotation.x = this.rotationX;
      this.rotationY += 0.01;
      this.plushie3d.rotation.y = this.rotationY;
    }

    this.renderer.render(this.scene, this.camera);
  };

  /**
   * Method to handle the click event on the toggle button
   */
  private toggleView(): void {
    this.mode = this.mode == PlushieDisplay3D ? PlushieDisplay2D : PlushieDisplay3D;
    this.plushie3d.rotation.set(0, 0, 0);
    this.rotationX = 0;
    this.rotationY = 0;
    this.setView();
  }

  private togglePlay(): void {
    this.isPlaying = !this.isPlaying;
    this.autoRotate = this.isPlaying;
    if(this.isPlaying) {
      this.playButton.classList.add("paused");
    } else {
      this.playButton.classList.remove("paused");
    }
  }


  /**
   * Method to handle the grab action on the mouse down event on the canvas
   * @param e MouseEvent
   */
  private grab(e:MouseEvent): void {
    this.isDragging = true;
    this.autoRotate = false;
    this.lastX = e.clientX;
    this.lastY = e.clientY;
  }

  /**
   * Method to handle the release action on either the mouse up or mouse out event on the canvas
   * @param e MouseEvent
   */
  private release(e:MouseEvent): void {
    if(this.isDragging) {
      this.isDragging = false;
      this.autoRotate = this.isPlaying;
    }
  }

  /**
   * Method to handle the drag action on the mouse move event on the canvas
   * @param e MouseEvent
   */
  private drag(e:MouseEvent): void {
    if (!this.isDragging || !this.plushie3d) return;
    const dx = e.clientX - this.lastX;
    const dy = e.clientY - this.lastY;
    this.lastX = e.clientX;
    this.lastY = e.clientY;
    this.rotationY += dx * 0.01;
    this.rotationX += dy * 0.01;
    this.rotationX = Math.max(-Math.PI/2, Math.min(Math.PI/2, this.rotationX));
    this.plushie3d.rotation.x = this.rotationX;
    this.plushie3d.rotation.y = this.rotationY;
  }

  /**
   * Method to render the plushie controls
   * @returns HTML Template
   */
  renderControls(): TemplateResult {
    return html`
      <div class="${this.hideControls ? "hidden" : ""}">
        <button 
          id="display"
          @click=${this.toggleView}
        >
          ${this.mode == PlushieDisplay3D ? PlushieDisplay2D : PlushieDisplay3D}
        </button>
        <button 
          id="play"
          @click=${this.togglePlay}
        >
        </button>
      </div>
    `;
  }

  /**
   * Render the plushie component
   * @returns HTML Template
   */
  render(): TemplateResult {
    return html`
      ${this.renderControls()}
      <canvas
        @mousedown=${(e:MouseEvent) => this.grab(e)}
        @mouseup=${(e:MouseEvent) => this.release(e)}
        @mouseout=${(e:MouseEvent) => this.release(e)}
        @mousemove=${(e:MouseEvent) => this.drag(e)}
      ></canvas>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "maz-plushie": MaziCraftPlushie;
  }
}
